import * as React from 'react';
import { v4 as uuid } from 'uuid';
import { isEqual, noop } from 'vet-bones/utils';

import { MAX_CSV_PARSE_LINES } from 'src/constants/limits';
import {
  ProjectFileUploadField,
  ProjectFileUploadOptions,
  ProjectFileUploadStatistics,
} from 'src/store/state/projects';
import { assignDistinctAndRecordStatistics } from 'src/utils/csv/assignDistinctAndRecordStatistics';
import { mergeEditedFieldLabels } from 'src/utils/csv/mergeEditedFieldLabels';
import {
  ExtendedParseError,
  FullFileStats,
  ParseCsvResult,
  parseCsv,
} from 'src/utils/csv/parseCsv';
import { useDidValueChange } from 'src/utils/hooks/useDidValueChange';

export const useProjectFileUploadParser = (
  file: string | File | null | undefined,
  fileOptions: ProjectFileUploadOptions,
  limitRows?: number,
  existingFields?: ProjectFileUploadField[],
  cachedFileStats?: FullFileStats
) => {
  const [errors, setErrors] = React.useState<ExtendedParseError[]>([]);
  const [fields, setFields] = React.useState<ProjectFileUploadField[]>(
    existingFields || []
  );
  const [rows, setRows] = React.useState<string[][]>([]);
  const [fullFileStats, setFullFileStats] = React.useState<
    FullFileStats | undefined
  >();
  const [isComputingStats, setIsComputingStats] = React.useState(false);

  const parsingIds = React.useRef<Set<string>>(new Set());
  const [isParsing, setIsParsing] = React.useState(false);

  const didCleanup = React.useRef<boolean>(false);
  const abortParser = React.useRef<() => void>(noop);
  const activeStatsId = React.useRef<string | null>(null);

  // Stabilize existingFields to prevent unnecessary re-runs
  const existingFieldsRef = React.useRef<ProjectFileUploadField[]>();
  if (!isEqual(existingFieldsRef.current, existingFields)) {
    existingFieldsRef.current = existingFields;
  }

  const cachedFileStatsRef = React.useRef(cachedFileStats);
  cachedFileStatsRef.current = cachedFileStats;

  React.useEffect(() => {
    didCleanup.current = false;
    abortParser.current();

    if (file && fileOptions.quote && fileOptions.separator) {
      const id = uuid();
      parsingIds.current.add(id);
      activeStatsId.current = id;
      setIsParsing(true);
      setFullFileStats(undefined);
      setIsComputingStats(false);
      // Clear previous parse output so a save during re-parse can't pick up
      // stale fields/rows from the previously-parsed file.
      setErrors([]);
      setFields([]);
      setRows([]);
      const { abort, parsePromise, statsPromise } =
        parseCsv<ProjectFileUploadStatistics>(
          file,
          {
            delimiter: fileOptions.separator,
            escapeChar: fileOptions.escape,
            header: fileOptions.header,
            quoteChar: fileOptions.quote,
          },
          { distinct: 0, records: 0 },
          assignDistinctAndRecordStatistics,
          limitRows
        );

      abortParser.current = abort;
      parsePromise.then(({ data, errors, fullFileStats: stats }) => {
        if (didCleanup.current) {
          return;
        }

        if (errors) {
          setErrors(errors);
          setFields([]);
          setRows([]);
          setFullFileStats(undefined);
        } else if (data) {
          setErrors([]);
          setFields(
            mergeEditedFieldLabels(data.fields, existingFieldsRef.current)
          );
          setRows(data.rows);
          if (stats) {
            setFullFileStats(stats);
          } else if (cachedFileStatsRef.current) {
            setFullFileStats(cachedFileStatsRef.current);
            abort();
          } else if (statsPromise) {
            setIsComputingStats(true);
          }
        }
        parsingIds.current.delete(id);
        setIsParsing(Boolean(parsingIds.current.size));
      });

      if (statsPromise && !cachedFileStatsRef.current) {
        statsPromise
          .then((stats) => {
            if (didCleanup.current || activeStatsId.current !== id) {
              return;
            }
            setFullFileStats(stats);
            setIsComputingStats(false);
          })
          .catch((statsErrors: ExtendedParseError[]) => {
            if (didCleanup.current || activeStatsId.current !== id) {
              return;
            }
            setErrors(statsErrors);
            setFullFileStats(undefined);
            setIsComputingStats(false);
          });
      }
    } else if (!didCleanup.current) {
      abortParser.current = noop;
      activeStatsId.current = null;
      setFields([]);
      setRows([]);
      setFullFileStats(undefined);
      setIsComputingStats(false);
    }

    return () => {
      didCleanup.current = true;
      abortParser.current();
    };
  }, [file, fileOptions, limitRows]);

  const updateFieldLabel = React.useCallback(
    (value: string, label: string) => {
      setFields(
        fields.map((field) => ({
          ...field,
          label: field.value === value ? label : field.label,
        }))
      );
    },
    [fields]
  );

  return {
    abortParser,
    fullFileStats,
    errors,
    fields,
    isComputingStats,
    isParsing,
    rows,
    updateFieldLabel,
  };
};

export const useProjectMultipleFileUploadsParser = (
  files: File[],
  fileOptions: ProjectFileUploadOptions
) => {
  const [errorsByFileIdx, setErrorsByFileIdx] = React.useState<
    Record<number, ExtendedParseError[]>
  >({});
  const [fieldsByFileIdx, setFieldsByFileIdx] = React.useState<
    Record<number, ProjectFileUploadField[]>
  >({});
  const [isPartialByFileIdx, setIsPartialByFileIdx] = React.useState<
    Record<number, boolean>
  >({});
  const [fullFileStatsByFileIdx, setFullFileStatsByFileIdx] = React.useState<
    Record<number, FullFileStats | undefined>
  >({});
  const [rowsByFileIdx, setRowsByFileIdx] = React.useState<
    Record<number, string[][]>
  >({});
  const [isComputingStats, setIsComputingStats] = React.useState(false);

  const parsingIds = React.useRef<Set<string>>(new Set());
  const [isParsing, setIsParsing] = React.useState(false);

  const didCleanup = React.useRef<boolean>(false);
  const abortParserFns = React.useRef<Array<() => void>>([]);
  const activeStatsId = React.useRef<string | null>(null);
  const didFilesChange = useDidValueChange(files, isEqual);
  const didOptionsChange = useDidValueChange(fileOptions, isEqual);

  React.useEffect(() => {
    didCleanup.current = false;

    if (didFilesChange || didOptionsChange) {
      abortParserFns.current.forEach((abortParser) => abortParser());
      if (files.length && fileOptions.quote && fileOptions.separator) {
        const id = uuid();
        parsingIds.current.add(id);
        activeStatsId.current = id;
        setIsParsing(true);
        setFullFileStatsByFileIdx({});
        setIsComputingStats(true);

        const parsePromises: Array<
          Promise<ParseCsvResult<ProjectFileUploadStatistics>>
        > = [];
        const statsPromises: Array<{
          idx: number;
          promise: Promise<FullFileStats | undefined>;
        }> = [];

        files.forEach((file, fileIdx) => {
          // Parse one row past the preview limit so we can detect whether the
          // file has more rows than we retain (and is therefore partial).
          const { abort, parsePromise, statsPromise } =
            parseCsv<ProjectFileUploadStatistics>(
              file,
              {
                delimiter: fileOptions.separator,
                escapeChar: fileOptions.escape,
                header: fileOptions.header,
                quoteChar: fileOptions.quote,
              },
              { distinct: 0, records: 0 },
              assignDistinctAndRecordStatistics,
              MAX_CSV_PARSE_LINES + 1
            );
          abortParserFns.current.push(abort);
          parsePromises.push(parsePromise);
          if (statsPromise) {
            statsPromises.push({ idx: fileIdx, promise: statsPromise });
          }
        });

        Promise.all(parsePromises).then((results) => {
          const nextErrorsByFileIdx: Record<number, ExtendedParseError[]> = {};
          const nextFieldsByFileIdx: Record<number, ProjectFileUploadField[]> =
            {};
          const nextIsPartialByFileIdx: Record<number, boolean> = {};
          const nextFullFileStatsByFileIdx: Record<
            number,
            FullFileStats | undefined
          > = {};
          const nextRowsByFileIdx: Record<number, string[][]> = {};
          let hasStatsFromPreview = false;
          results.forEach(({ data, errors, fullFileStats }, idx) => {
            if (didCleanup.current) {
              return;
            }
            if (errors) {
              // this looks dumb, but it looks like papaparse reuses the same ref for
              // data && errors (so previous errors get cleared if we use the errors arr directly)
              nextErrorsByFileIdx[idx] = [...errors];
              nextFieldsByFileIdx[idx] = [];
              nextRowsByFileIdx[idx] = [];
            } else if (data) {
              // Partial when the file has more rows than the preview we retain.
              // We parsed MAX_CSV_PARSE_LINES + 1 to detect overflow, so keep
              // only the first MAX_CSV_PARSE_LINES rows.
              const isPartial = data.rows.length > MAX_CSV_PARSE_LINES;
              const previewRows = isPartial
                ? data.rows.slice(0, MAX_CSV_PARSE_LINES)
                : data.rows;
              // parseCsv computed statistics over the extra overflow row; when
              // partial, recompute over only the rows we keep so the shown
              // records/distinct match the preview table.
              const previewFields = isPartial
                ? data.fields.map((field) => ({
                    ...field,
                    statistics: { ...field.statistics },
                  }))
                : data.fields;
              if (isPartial) {
                assignDistinctAndRecordStatistics(previewFields, previewRows);
              }
              nextErrorsByFileIdx[idx] = [];
              nextFieldsByFileIdx[idx] = previewFields;
              nextIsPartialByFileIdx[idx] = isPartial;
              if (fullFileStats) {
                nextFullFileStatsByFileIdx[idx] = fullFileStats;
                hasStatsFromPreview = true;
              }
              nextRowsByFileIdx[idx] = previewRows;
            }
          });
          setErrorsByFileIdx(nextErrorsByFileIdx);
          setFieldsByFileIdx(nextFieldsByFileIdx);
          setIsPartialByFileIdx(nextIsPartialByFileIdx);
          if (hasStatsFromPreview) {
            setFullFileStatsByFileIdx(nextFullFileStatsByFileIdx);
          }
          setRowsByFileIdx(nextRowsByFileIdx);
          parsingIds.current.delete(id);
          setIsParsing(Boolean(parsingIds.current.size));

          if (statsPromises.length > 0) {
            setIsComputingStats(true);
          }
        });

        if (statsPromises.length > 0) {
          Promise.allSettled(statsPromises.map((s) => s.promise)).then(
            (results) => {
              if (didCleanup.current || activeStatsId.current !== id) {
                return;
              }
              const nextStats: Record<number, FullFileStats | undefined> = {};
              const lateErrors: Record<number, ExtendedParseError[]> = {};
              results.forEach((result, i) => {
                const fileIdx = statsPromises[i].idx;
                if (result.status === 'fulfilled') {
                  nextStats[fileIdx] = result.value;
                } else {
                  lateErrors[fileIdx] = result.reason;
                }
              });
              setFullFileStatsByFileIdx(nextStats);
              if (Object.keys(lateErrors).length) {
                setErrorsByFileIdx((prev) => ({ ...prev, ...lateErrors }));
              }
              setIsComputingStats(false);
            }
          );
        }
      } else {
        abortParserFns.current = [];
        activeStatsId.current = null;
        setFieldsByFileIdx({});
        setRowsByFileIdx({});
        setIsPartialByFileIdx({});
        setFullFileStatsByFileIdx({});
        setIsComputingStats(false);
      }
    }

    return () => {
      didCleanup.current = true;
      abortParserFns.current.forEach((abortParser) => abortParser());
    };
  }, [
    didFilesChange,
    didOptionsChange,
    fileOptions.escape,
    fileOptions.header,
    fileOptions.quote,
    fileOptions.separator,
    files,
  ]);

  const updateFieldLabelForFileIdx = React.useCallback(
    (idx: number, value: string, label: string) => {
      if (!fieldsByFileIdx[idx]) {
        return;
      }
      setFieldsByFileIdx({
        ...fieldsByFileIdx,
        [idx]: fieldsByFileIdx[idx].map((field) => ({
          ...field,
          label: field.value === value ? label : field.label,
        })),
      });
    },
    [fieldsByFileIdx]
  );

  return {
    abortParserFns: abortParserFns.current,
    fullFileStatsByFileIdx,
    errorsByFileIdx,
    fieldsByFileIdx,
    isComputingStats,
    isParsing,
    isPartialByFileIdx,
    rowsByFileIdx,
    updateFieldLabelForFileIdx,
  };
};
